import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, Gift } from "lucide-react";

import { gradientFor, initialsFor } from "../../components/Avatar/avatarLook";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { Pill } from "../../components/Pill/Pill";
import { Skeleton } from "../../components/Skeleton/Skeleton";
import { Table, type Column } from "../../components/Table/Table";
import {
  operatorBadgeFor,
  operatorTitleFor,
  windowBadgeFor,
} from "../leaderboards/boardBadges";
import ec from "../leaderboards/EntityCell.module.css";
import lbl from "../leaderboards/LeaderboardsListPage.module.css";

import { formatUTC } from "./formatTime";
import type {
  GuildBoardRow,
  PlayerBoardReward,
  PlayerBoardRow,
  PlayerCycleRef,
  PlayerLeaderboards,
  PlayerBoardRef,
} from "./types";
import card from "./PlayerReferralsCard.module.css";
import styles from "./PlayerLeaderboardsCard.module.css";

type Guild = NonNullable<PlayerLeaderboards["guild"]>;
type BoardRow = PlayerBoardRow | GuildBoardRow;

// === Formatting ===================================================== //

const MINUTE = 60_000;
const DAY_MINUTES = 24 * 60;

function fmtNumber(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * "3d 6h", "6h 12m", "40m" — how long the cycle has left. A week or more out
 * the hours are noise, so only the days are kept.
 */
function formatEndsIn(minutes: number): string {
  const days = Math.floor(minutes / DAY_MINUTES);
  const hours = Math.floor((minutes % DAY_MINUTES) / 60);
  const mins = minutes % 60;
  if (days >= 7) return `${days}d`;
  if (days >= 1) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  if (hours >= 1) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  return `${mins}m`;
}

// === Component ====================================================== //

interface Props {
  projectId: string;
  /** The answer, or undefined while the request is in flight. */
  data: PlayerLeaderboards | undefined;
  loading: boolean;
  failed: boolean;
}

/**
 * The cycles running right now that the player stands on — by themselves, and
 * through their guild — with the place they hold and what it would pay.
 *
 * Read-only: a row opens the board's Cycles tab on that cycle, which is where
 * a score is corrected. The query lives on the page, and the card draws its
 * own loading and failure states like its neighbours.
 */
export function PlayerLeaderboardsCard({
  projectId,
  data,
  loading,
  failed,
}: Props) {
  const hrefOf = (row: BoardRow) =>
    `/projects/${projectId}/leaderboards/${row.leaderboard.id}?${new URLSearchParams(
      { tab: "cycles", cycle: row.cycle.id },
    ).toString()}`;

  let body: ReactNode;
  let sub: ReactNode = null;

  if (failed && !data) {
    body = (
      <div className={card.note} role="status">
        Couldn&apos;t load leaderboards.
      </div>
    );
  } else if (loading || !data) {
    body = <LeaderboardsSkeleton />;
  } else {
    const { guild, player_boards: mine, guild_boards: ours } = data;
    const total = mine.length + ours.length;

    if (total === 0) {
      body = (
        <EmptyState
          title="Not on any leaderboard right now"
          description="No score in a running cycle — neither the player's own nor their guild's."
        />
      );
    } else {
      sub = <span className={styles.cardSub}>{total} running now</span>;
      // Without a guild there is one list only, and a heading over it would
      // name a distinction the card has nothing to draw.
      const grouped = guild !== null;
      body = (
        <>
          <div className={styles.hint}>
            Cycles running right now. <b>Place</b> is where the player stands
            in the part of the board they compete in — league, room, personal
            list or guild. <b>Reward now</b> is what they&apos;d get if the
            cycle ended this minute.
          </div>

          {mine.length > 0 && (
            <>
              {grouped && (
                <div className={styles.group}>
                  <span className={card.kicker}>Player boards</span>
                  <span className={styles.count}>{mine.length}</span>
                </div>
              )}
              <Table
                columns={playerColumns(guild, hrefOf)}
                rows={mine}
                rowClassName={() => styles.row}
                rowHref={hrefOf}
              />
            </>
          )}

          {guild !== null && ours.length > 0 && (
            <>
              <div
                className={`${styles.group}${
                  mine.length > 0 ? ` ${styles.groupGap}` : ""
                }`}
              >
                <span className={card.kicker}>Guild boards ·</span>
                <Link
                  to={`/projects/${projectId}/guilds/${guild.id}`}
                  className={`${ec.link} ${styles.groupGuild}`}
                >
                  <GuildEmblem guild={guild} />
                  {guild.name}
                </Link>
                <span className={styles.count}>{ours.length}</span>
              </div>
              <Table
                columns={guildColumns(hrefOf)}
                rows={ours}
                rowClassName={() => styles.row}
                rowHref={hrefOf}
              />
            </>
          )}
        </>
      );
    }
  }

  return (
    <div className={card.card}>
      <div className={card.cardHeader}>
        <h3 className={card.cardTitle}>Leaderboards</h3>
        {sub}
      </div>
      <div className={card.cardBody}>{body}</div>
    </div>
  );
}

// === Columns ======================================================== //

/**
 * The chevron is the row's real link: the one stop for the keyboard, and what
 * a screen reader announces, while the row itself takes the mouse.
 */
function goColumn<R extends BoardRow>(hrefOf: (row: R) => string): Column<R> {
  return {
    key: "go",
    header: <span hidden>Open</span>,
    width: "34px",
    cellClassName: styles.goCell,
    cell: (r) => (
      <Link
        to={hrefOf(r)}
        className={styles.go}
        aria-label={`Open ${r.leaderboard.name}`}
      >
        <ChevronRight size={15} aria-hidden />
      </Link>
    ),
  };
}

function playerColumns(
  guild: Guild | null,
  hrefOf: (row: PlayerBoardRow) => string,
): Column<PlayerBoardRow>[] {
  return [
    {
      key: "board",
      header: "Leaderboard",
      cell: (r) => <BoardCell board={r.leaderboard} cycle={r.cycle} />,
    },
    {
      key: "place",
      header: "Place",
      width: "170px",
      cell: (r) => <PlayerPlace row={r} guild={guild} />,
    },
    {
      key: "score",
      header: "Score",
      width: "120px",
      align: "right",
      cell: (r) => <Score board={r.leaderboard} score={r.score} />,
    },
    {
      key: "reward",
      header: "Reward now",
      width: "180px",
      cell: (r) => <Rewards rewards={r.rewards} />,
    },
    goColumn(hrefOf),
  ];
}

function guildColumns(
  hrefOf: (row: GuildBoardRow) => string,
): Column<GuildBoardRow>[] {
  return [
    {
      key: "board",
      header: "Leaderboard",
      cell: (r) => <BoardCell board={r.leaderboard} cycle={r.cycle} />,
    },
    {
      key: "place",
      header: "Guild place",
      width: "120px",
      cell: (r) =>
        r.rank === null ? (
          <None />
        ) : (
          <div className={`${styles.place}${r.rank <= 3 ? ` ${styles.top}` : ""}`}>
            #{fmtNumber(r.rank)}
          </div>
        ),
    },
    {
      key: "score",
      header: "Guild score",
      width: "130px",
      align: "right",
      cell: (r) => <Score board={r.leaderboard} score={r.score} />,
    },
    {
      key: "share",
      header: "Player's share",
      width: "190px",
      cell: (r) => <Share share={r.share} />,
    },
    {
      key: "reward",
      header: "Reward now",
      width: "150px",
      cell: (r) => <Rewards rewards={r.rewards} />,
    },
    goColumn(hrefOf),
  ];
}

// === Cells ========================================================== //

function None() {
  return <span className={styles.none}>—</span>;
}

function GuildEmblem({ guild }: { guild: Guild }) {
  return (
    <span
      className={`${ec.avatar} ${ec.emblem} ${styles.emblemXs}`}
      style={{ background: gradientFor(guild.name) }}
      aria-hidden="true"
    >
      {initialsFor(guild.name)}
    </span>
  );
}

/** The board's name over its key, its window and how long the cycle has left. */
function BoardCell({
  board,
  cycle,
}: {
  board: PlayerBoardRef;
  cycle: PlayerCycleRef;
}) {
  const wb = windowBadgeFor(board);
  const tint =
    wb.variant === "info"
      ? lbl.badgeInfo
      : wb.variant === "warn"
        ? lbl.badgeWarn
        : lbl.badgeNeutral;
  return (
    <div className={styles.board}>
      <div className={styles.boardName}>
        <span className={styles.name}>{board.name}</span>
        {board.status === "paused" && (
          <span className={`${lbl.statusPill} ${lbl.statusPaused}`}>
            <span className={lbl.statusDot} aria-hidden="true" />
            paused
          </span>
        )}
      </div>
      <div className={styles.boardMeta}>
        <span className={styles.key}>{board.ext_key}</span>
        <span className={styles.sep}>·</span>
        <span className={`${lbl.badge} ${tint} ${styles.badgeSm}`}>
          {wb.text}
        </span>
        {cycle.status === "closing" ? (
          <Closing />
        ) : (
          cycle.close_at !== null && <EndsIn closeAt={cycle.close_at} />
        )}
      </div>
    </div>
  );
}

/** A cycle past its end whose results are still being counted. */
function Closing() {
  return (
    <>
      <span className={styles.sep}>·</span>
      <span className={`${styles.ends} ${styles.endsSoon}`}>closing</span>
    </>
  );
}

function EndsIn({ closeAt }: { closeAt: string }) {
  const close = new Date(closeAt).getTime();
  if (Number.isNaN(close)) return null;
  const left = Math.max(0, Math.floor((close - Date.now()) / MINUTE));
  const soon = left < DAY_MINUTES;
  return (
    <>
      <span className={styles.sep}>·</span>
      <span
        className={`${styles.ends}${soon ? ` ${styles.endsSoon}` : ""}`}
        title={formatUTC(closeAt)}
      >
        ends in {formatEndsIn(left)}
      </span>
    </>
  );
}

/** The player's place, and which part of the board it is counted in. */
function PlayerPlace({
  row,
  guild,
}: {
  row: PlayerBoardRow;
  guild: Guild | null;
}) {
  if (row.rank === null) return <None />;
  const { segment } = row;
  const personal = segment.kind === "personal";
  return (
    <>
      <div className={`${styles.place}${row.rank <= 3 ? ` ${styles.top}` : ""}`}>
        #{fmtNumber(row.rank)}
        {personal && segment.of !== undefined && (
          <>
            {" "}
            <span className={styles.of}>of {fmtNumber(segment.of)}</span>
          </>
        )}
      </div>
      <SegmentCaption segment={segment} guild={guild} />
    </>
  );
}

function SegmentCaption({
  segment,
  guild,
}: {
  segment: PlayerBoardRow["segment"];
  guild: Guild | null;
}) {
  const tier = segment.tier_name ?? segment.tier_key;
  const room = segment.room !== undefined ? fmtNumber(segment.room) : "";
  switch (segment.kind) {
    case "tier_room":
      return (
        <div className={styles.seg}>
          <span className={styles.tier}>{tier}</span> · room {room}
        </div>
      );
    case "tier":
      return (
        <div className={styles.seg}>
          <span className={styles.tier}>{tier}</span>
        </div>
      );
    case "room":
      return (
        <div className={styles.seg}>
          room {room}
        </div>
      );
    case "personal":
      return <div className={styles.seg}>personal list</div>;
    case "guild":
      return (
        <div className={styles.seg}>
          in {guild && <GuildEmblem guild={guild} />}
          {guild?.name ?? "their guild"}
        </div>
      );
    default:
      return <div className={styles.seg}>whole board</div>;
  }
}

function Score({ board, score }: { board: PlayerBoardRef; score: number }) {
  return (
    <span className={styles.score}>
      <span className={`${lbl.glyph} ${styles.op}`} title={operatorTitleFor(board)}>
        {operatorBadgeFor(board).glyph}
      </span>
      {fmtNumber(score)}
    </span>
  );
}

function Rewards({ rewards }: { rewards: PlayerBoardReward[] }) {
  if (rewards.length === 0) return <None />;
  return (
    <span className={styles.rewards}>
      {rewards.map((r) => (
        <Pill key={r.id} size="sm" variant="success" className={styles.reward}>
          <Gift size={11} aria-hidden />
          {r.name}
        </Pill>
      ))}
    </span>
  );
}

/** How much of the guild's score is the player's own. */
function Share({ share }: { share: GuildBoardRow["share"] }) {
  if (share.source === "submit") {
    return (
      <span
        className={styles.shareNone}
        title="The guild submits this score itself, so no part of it belongs to one member."
      >
        — guild submits
      </span>
    );
  }
  return (
    <>
      <div className={styles.share}>
        {fmtNumber(share.score ?? 0)}
        {share.percent != null && (
          <span className={styles.pct}>
            {share.percent.toLocaleString("en-US", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
            })}
            %
          </span>
        )}
      </div>
      {share.source === "member_contribution" && share.member_rank != null && (
        <div className={styles.shareSub}>
          #{fmtNumber(share.member_rank)} of{" "}
          {fmtNumber(share.member_count ?? 0)} in the guild
        </div>
      )}
      {share.source === "player_stat" && (
        <div className={styles.shareSub}>
          <span className={styles.shareKey}>{share.stat_key}</span> stat
        </div>
      )}
    </>
  );
}

// === Loading skeleton =============================================== //

/** Three rows in the table's frame — the handful a card usually holds. */
function LeaderboardsSkeleton() {
  return (
    <div className={styles.skelRows}>
      {[
        [140, 190],
        [120, 170],
        [150, 150],
      ].map(([name, meta], i) => (
        <div className={card.skelRow} key={i}>
          <div className={styles.skelLines}>
            <Skeleton width={name} height={13} />
            <Skeleton width={meta} height={10} />
          </div>
          <Skeleton width={40} height={16} />
          <Skeleton width={56} height={13} />
          <Skeleton width={84} height={18} radius={999} />
        </div>
      ))}
    </div>
  );
}
